/**
 * KAN-980 / KAN-979: API раздела "Платформа" (только суперпользователь):
 * кабинеты, ручное начисление и списание кошелька, пул Telegram-аккаунтов
 * (выдача кабинету и возврат в пул). Ошибки axios (400/409) не перехватываются.
 */
import apiClient from './client';
import type { OrganizationMode } from '@/types/settings';

const BASE = '/api/v1/platform';

export interface PlatformOrganization {
  id: string;
  name: string;
  mode: OrganizationMode | string;
  plan_name: string | null;
  /** Остаток кошелька строкой ("1500.00"). */
  balance: string;
  users_count: number;
  telegram_accounts_count: number;
  /** KAN-997: подключено, были под флудом, здоровых (без списанных). */
  telegram_accounts_connected?: number;
  telegram_accounts_flooded?: number;
  telegram_accounts_healthy?: number;
  is_pool: boolean;
}

export interface PlatformOperator {
  id: string;
  email: string;
  name: string;
  template: string | null;
  accounts_count: number;
}

export interface WalletAdjustmentPayload {
  /** Сумма со знаком строкой с двумя знаками: "500.00", "-100.50". */
  amount: string;
  comment: string;
}

export interface WalletAdjustmentResult {
  balance_before: string;
  balance_after: string;
  amount: string;
  entry_id: string;
}

export type TgPoolScope = 'pool' | 'all';

export interface TgPoolAccount {
  account_id: string;
  phone: string | null;
  label: string | null;
  name?: string | null;
  connected: boolean;
  status: string | null;
  connected_at: string | null;
  connected_via: string | null;
  seller: string | null;
  has_proxy: boolean;
  outreach: boolean;
  disabled: boolean;
  decommissioned: boolean;
  personal: boolean;
  flags: string[];
  age_days: number | null;
  fresh: boolean;
  in_pool: boolean;
  organization_id: string | null;
  organization_name: string | null;
  operator_id: string | null;
  operator_email: string | null;
  /** KAN-997: история флуда и признак здорового аккаунта. */
  flood_flagged?: boolean;
  flood_first_at?: string | null;
  flood_last_at?: string | null;
  flood_episodes?: number;
  flood_cleared_at?: string | null;
  flood_cleared_by?: string;
  healthy?: boolean;
}

/** KAN-997: итоги по выдаче пула (считает бэкенд). */
export interface TgPoolSummary {
  connected: number;
  flooded: number;
  healthy: number;
}

export interface TgPoolResponse {
  pool_configured: boolean;
  pool_organization: { id: string; name: string } | null;
  accounts: TgPoolAccount[];
  summary?: TgPoolSummary;
}

export interface ClearFloodResultRow {
  account_id: string;
  /** cleared / not_flagged / failed / not_found. */
  status: string;
  error: string;
}

export interface ClearFloodResponse {
  results: ClearFloodResultRow[];
}

export interface AssignTgAccountsPayload {
  account_ids: string[];
  target_operator_id: string;
  confirm_fresh?: boolean;
}

export interface ReturnTgAccountsPayload {
  account_ids: string[];
}

export interface TransferResultRow {
  account_id: string;
  /** moved / refused / failed (статусы сервиса переноса KAN-974). */
  status: string;
  error: string;
  label: string | null;
  phone: string | null;
}

export interface TransferResponse {
  results: TransferResultRow[];
  warnings: string[];
}

/** Свежий аккаунт из ответа 409 requires_confirm. */
export interface FreshAccount {
  account_id: string;
  phone: string | null;
  label: string | null;
  connected_at: string | null;
}

export async function getPlatformOrganizations(): Promise<PlatformOrganization[]> {
  const response = await apiClient.get(`${BASE}/organizations/`);
  return response.data;
}

export async function getPlatformOrganizationOperators(orgId: string): Promise<PlatformOperator[]> {
  const response = await apiClient.get(`${BASE}/organizations/${orgId}/operators/`);
  return response.data;
}

export async function adjustPlatformWallet(
  orgId: string,
  payload: WalletAdjustmentPayload
): Promise<WalletAdjustmentResult> {
  const response = await apiClient.post(`${BASE}/organizations/${orgId}/wallet/`, payload);
  return response.data;
}

export async function getPlatformTgPool(scope: TgPoolScope): Promise<TgPoolResponse> {
  const response = await apiClient.get(`${BASE}/tg-pool/`, { params: { scope } });
  return response.data;
}

export async function assignPlatformTgAccounts(payload: AssignTgAccountsPayload): Promise<TransferResponse> {
  const response = await apiClient.post(`${BASE}/tg-pool/assign/`, payload);
  return response.data;
}

export async function returnPlatformTgAccounts(payload: ReturnTgAccountsPayload): Promise<TransferResponse> {
  const response = await apiClient.post(`${BASE}/tg-pool/return/`, payload);
  return response.data;
}

/** KAN-997: снять пометку "Был под флудом" (только суперпользователь). */
export async function clearTgFloodMarks(account_ids: string[]): Promise<ClearFloodResponse> {
  const response = await apiClient.post(`${BASE}/tg-pool/clear-flood/`, { account_ids });
  return response.data;
}

/**
 * KAN-1000: цены платформы (курс доллара и наценки). Десятичные приходят и
 * уходят строками; telephony_rub_per_min пустой = телефония без наценки.
 */
export interface PlatformPricing {
  usd_rub_rate: string;
  llm_markup: string;
  voice_markup: string;
  telephony_rub_per_min: string | null;
  updated_at: string;
}

export type PlatformPricingField = Exclude<keyof PlatformPricing, 'updated_at'>;

export type PlatformPricingPatch = Partial<Pick<PlatformPricing, PlatformPricingField>>;

export async function getPlatformPricing(): Promise<PlatformPricing> {
  const response = await apiClient.get(`${BASE}/pricing/`);
  return response.data;
}

export async function updatePlatformPricing(patch: PlatformPricingPatch): Promise<PlatformPricing> {
  const response = await apiClient.patch(`${BASE}/pricing/`, patch);
  return response.data;
}
